import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  GitBranch,
  LayoutDashboard,
  ScanSearch,
  Bug,
  Sparkles,
  Shield,
  Calendar,
  Globe2,
  Package,
  ChevronDown,
} from 'lucide-react';
import { useApplication } from '../../contexts/ApplicationContext';
// Branch selector disabled for now — we don't select branches per repository and
// findings aren't keyed to branches. Restore these when it becomes relevant.
// import { useOrganization } from '../../contexts/OrganizationContext';
// import {
//   getPrimaryRepository,
//   isLegacyMultiRepo,
// } from '../../utils/repository';
import RepositorySwitcher from '../applications/RepositorySwitcher';
// import BranchSelector from '../applications/BranchSelector';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  /** Match the route exactly (e.g. the overview, whose path prefixes every sub-route). */
  exact?: boolean;
}

interface ApplicationSidebarProps {
  /**
   * When true, render a narrow icon rail that expands to the full sidebar on hover
   * (overlaying the content to its right). Used alongside the findings sidebar.
   */
  collapsed?: boolean;
}

const ApplicationSidebar: React.FC<ApplicationSidebarProps> = ({ collapsed = false }) => {
  const location = useLocation();
  const { applicationId, error } = useApplication();

  // Branch selector disabled for now (see import note above).
  // const { currentOrganization } = useOrganization();
  //
  // const isReadOnly =
  //   !!currentOrganization?.is_demo ||
  //   currentOrganization?.current_user_role === 'viewer';
  //
  // // Branch switcher applies to a single primary repository (not legacy multi-repo apps).
  // const primaryRepo = currentApplication ? getPrimaryRepository(currentApplication) : null;
  // const branchSelector =
  //   currentApplication && primaryRepo && currentOrganization && !isLegacyMultiRepo(currentApplication) ? (
  //     <BranchSelector
  //       variant="sidebar"
  //       collapsed={collapsed}
  //       organizationId={currentOrganization.id}
  //       applicationId={applicationId!}
  //       repoFullName={primaryRepo.full_name}
  //       provider={primaryRepo.provider || 'github'}
  //       currentBranch={primaryRepo.default_branch}
  //       gitlabInstanceUrl={
  //         primaryRepo.provider === 'gitlab' && primaryRepo.html_url
  //           ? new URL(primaryRepo.html_url).origin
  //           : undefined
  //       }
  //       readOnly={isReadOnly}
  //       onBranchUpdated={() => refreshApplication()}
  //     />
  //   ) : null;

  // Primary navigation (always visible)
  const primaryNav: NavItem[] = applicationId ? [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard, path: `/repositories/${applicationId}`, exact: true },
    { id: 'scans', label: 'Scans', icon: ScanSearch, path: `/repositories/${applicationId}/scans` },
    { id: 'findings', label: 'Findings', icon: Bug, path: `/repositories/${applicationId}/findings` },
    { id: 'fixes', label: 'Fixes', icon: Sparkles, path: `/repositories/${applicationId}/fixes` },
  ] : [];

  // Secondary tools, grouped under a collapsible "More tools" section
  const moreToolsNav: NavItem[] = applicationId ? [
    { id: 'sbom', label: 'SBOM', icon: Package, path: `/repositories/${applicationId}/sbom` },
    { id: 'repositories', label: 'Repositories', icon: GitBranch, path: `/repositories/${applicationId}/settings/source` },
    { id: 'security-context', label: 'Security Context', icon: Shield, path: `/repositories/${applicationId}/settings/security-context` },
    { id: 'schedules', label: 'Schedules', icon: Calendar, path: `/repositories/${applicationId}/settings/schedules` },
    { id: 'public-status', label: 'Public Status', icon: Globe2, path: `/repositories/${applicationId}/settings/public-status` },
  ] : [];

  const isActive = (item: NavItem) =>
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  const moreToolsActive = moreToolsNav.some(isActive);
  const [moreOpen, setMoreOpen] = useState(false);
  // Keep the section open while one of its items is the active route.
  const moreExpanded = moreOpen || moreToolsActive;

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item);
    return (
      <Link
        key={item.id}
        to={item.path}
        title={collapsed ? item.label : undefined}
        className={`w-full flex items-center gap-[11px] rounded-[9px] text-[13.5px] font-medium transition-colors ${
          collapsed ? 'px-2 py-[9px] justify-center group-hover:justify-start' : 'px-[11px] py-[9px]'
        } ${
          active
            ? 'bg-[var(--lime-soft)] text-[color:var(--accent-ink)]'
            : 'text-[color:var(--text-secondary)] hover:bg-[var(--surface-2)] hover:text-[color:var(--text-primary)]'
        }`}
      >
        <item.icon size={16} className="flex-shrink-0" />
        {/* Hidden in the collapsed rail; revealed when it hover-expands. */}
        <span className={collapsed ? 'hidden group-hover:inline whitespace-nowrap' : ''}>{item.label}</span>
      </Link>
    );
  };

  const content = (
    <>
      {/* Repository + branch switchers */}
      <div className={`space-y-2 ${collapsed ? 'p-3' : 'p-4'}`} style={{ borderBottom: '1px solid var(--divider)' }}>
        <RepositorySwitcher collapsed={collapsed} />
        {/* {branchSelector} — branch selector disabled for now */}
        {error && !collapsed && <div className="text-xs" style={{ color: 'var(--danger)' }}>{error}</div>}
      </div>

      {/* Navigation */}
      <nav className={`flex-1 overflow-y-auto ${collapsed ? 'p-2' : 'p-3'}`}>
        {/* Primary */}
        <div className="space-y-1">
          {primaryNav.map(renderNavItem)}
        </div>

        {/* Divider */}
        <div className="my-3" style={{ borderTop: '1px solid var(--divider)' }} />

        {/* More tools */}
        {collapsed ? (
          // In the icon rail, surface the tools as a flat icon list.
          <div className="space-y-1">{moreToolsNav.map(renderNavItem)}</div>
        ) : (
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => setMoreOpen((open) => !open)}
              aria-expanded={moreExpanded}
              className="w-full flex items-center px-[11px] py-[9px] rounded-[9px] text-[12.5px] font-medium transition-colors text-[color:var(--text-muted)] hover:bg-[var(--surface-2)]"
            >
              <span>More tools</span>
              <ChevronDown
                size={14}
                className={`ml-auto flex-shrink-0 transition-transform ${moreExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            {moreExpanded && (
              <div className="pl-2 space-y-1">
                {moreToolsNav.map(renderNavItem)}
              </div>
            )}
          </div>
        )}
      </nav>
    </>
  );

  // Collapsed icon rail that expands to the full sidebar on hover, overlaying the
  // content to its right (so the findings sidebar keeps its place).
  if (collapsed) {
    return (
      <div className="group relative hidden md:block w-16 flex-shrink-0 h-full">
        <aside
          aria-label="Repository navigation"
          className="absolute inset-y-0 left-0 z-40 flex flex-col w-16 group-hover:w-[236px] overflow-hidden transition-[width] duration-200 ease-out shadow-sm group-hover:shadow-xl"
          style={{ background: 'var(--bg-raised)', borderRight: '1px solid var(--divider)', fontFamily: 'var(--font-sans)' }}
        >
          {content}
        </aside>
      </div>
    );
  }

  return (
    <aside
      className="w-[236px] h-full flex flex-col"
      style={{ background: 'var(--bg-raised)', borderRight: '1px solid var(--divider)', fontFamily: 'var(--font-sans)' }}
    >
      {content}
    </aside>
  );
};

export default ApplicationSidebar;
